import React, { useState, useEffect, useRef, useCallback } from 'react';
import Award from 'lucide-react/dist/esm/icons/award';
import ArrowUp from 'lucide-react/dist/esm/icons/arrow-up';
import ArrowDown from 'lucide-react/dist/esm/icons/arrow-down';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import Info from 'lucide-react/dist/esm/icons/info';
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw';
import { Link } from 'react-router-dom';
import { Card } from '../common/Card';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { ScoreService } from '../../lib/scoreService';

interface IDecideScoreWidgetProps {
  className?: string;
}

type EstateCounts = {
  passwords: number;
  documents: number;
  deputies: number;
};

export function IDecideScoreWidget({ className = '' }: IDecideScoreWidgetProps) {
  const { user, profile, refreshProfile } = useAuth();
  const [score, setScore] = useState(0);
  const [previousScore, setPreviousScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [counts, setCounts] = useState<EstateCounts | null>(null);
  /** Per user: seed previous score once so delta stays 0 until the user hits refresh. */
  const scoreBaselineUserRef = useRef<string | null>(null);

  const buildRecommendations = useCallback(
    (passwordCount: number, documentCount: number, deputyCount: number, currentScore: number) => {
      const recs: string[] = [];

      if (passwordCount < 5) {
        recs.push('Add more passwords to your digital vault (aim for at least five).');
      }
      if (documentCount < 3) {
        recs.push('Create or upload essential legal documents in the Legal section.');
      }
      if (deputyCount < 1) {
        recs.push('Designate at least one trusted deputy in Digital Estate contacts.');
      }
      if (currentScore <= 50) {
        recs.push('Complete onboarding preferences so your score reflects your full situation.');
      }

      const generalRecs = [
        'Review your digital assets and remove outdated logins.',
        'Confirm emergency contacts have up-to-date details.',
        'Review advance care and medical information in Health.',
        'Reconcile key financial accounts in Financial.',
        'Walk through the Digital Estate checklist for gaps.',
      ];

      const pool = generalRecs.filter((r) => !recs.includes(r));
      for (let i = 0; i < pool.length && recs.length < 3; i++) {
        recs.push(pool[i]);
      }

      return recs.slice(0, 3);
    },
    []
  );

  useEffect(() => {
    if (!user) {
      // Don't wipe data on transient null — only reset baseline tracking
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadCounts = async () => {
      // Only show skeleton if we've never loaded counts before
      if (counts === null) setLoading(true);
      try {
        const [pwRes, docRes, depRes, altDepRes] = await Promise.all([
          supabase
            .from('digital_assets')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('type', 'password'),
          supabase
            .from('legal_documents')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id),
          supabase
            .from('contacts')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('is_deputy', true),
          supabase
            .from('digital_estate_contacts')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id),
        ]);

        if (pwRes.error) {
          console.warn('IDecideScoreWidget: password count', pwRes.error.message);
        }
        if (docRes.error) {
          console.warn('IDecideScoreWidget: documents count', docRes.error.message);
        }
        if (depRes.error) {
          console.warn('IDecideScoreWidget: deputies (contacts)', depRes.error.message);
        }
        if (altDepRes.error) {
          console.warn('IDecideScoreWidget: deputies (digital_estate_contacts)', altDepRes.error.message);
        }

        if (!cancelled) {
          setCounts({
            passwords: pwRes.count ?? 0,
            documents: docRes.count ?? 0,
            deputies: Math.max(depRes.count ?? 0, altDepRes.count ?? 0),
          });
        }
      } catch (e) {
        console.warn('IDecideScoreWidget: count queries failed', e);
        if (!cancelled) {
          setCounts({ passwords: 0, documents: 0, deputies: 0 });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCounts();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) {
      setRecommendations([]);
      return;
    }
    if (counts === null) {
      return;
    }

    const current = profile?.idecide_score ?? 0;
    setRecommendations(
      buildRecommendations(counts.passwords, counts.documents, counts.deputies, current)
    );
  }, [user, counts, profile?.idecide_score, buildRecommendations]);

  useEffect(() => {
    if (!user || profile?.idecide_score === undefined || profile?.idecide_score === null) {
      return;
    }
    setScore(profile.idecide_score);
    if (scoreBaselineUserRef.current !== user.id) {
      setPreviousScore(profile.idecide_score);
      scoreBaselineUserRef.current = user.id;
    }
  }, [user, profile?.idecide_score]);

  const handleRefreshScore = async () => {
    if (!user) return;

    setRefreshError(null);
    const baseline = score;
    try {
      setRefreshing(true);
      await ScoreService.updateUserScore(user.id);
      if (refreshProfile) {
        await refreshProfile();
      }
      const { data: row, error } = await supabase
        .from('profiles')
        .select('idecide_score')
        .eq('id', user.id)
        .maybeSingle();
      if (error) {
        throw error;
      }
      const next = row?.idecide_score ?? baseline;
      setPreviousScore(baseline);
      setScore(next);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Could not refresh your score. Try again.';
      setRefreshError(message);
      console.error('Error refreshing score:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const getScoreColor = () => {
    if (score >= 400) return 'text-green-600';
    if (score >= 250) return 'text-blue-600';
    if (score >= 100) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreLabel = () => {
    if (score >= 400) return 'Planning Pro';
    if (score >= 250) return 'Well Prepared';
    if (score >= 100) return 'Making Progress';
    return 'Getting Started';
  };

  const getScoreDescription = () => {
    if (score >= 400) {
      return "You've taken excellent steps to prepare your digital estate!";
    }
    if (score >= 250) {
      return "You're well on your way to having a complete plan.";
    }
    if (score >= 100) {
      return "You've made a good start on your preparedness journey.";
    }
    return "Let's start building your preparedness plan.";
  };

  const scoreChange = score - previousScore;
  const scorePercentage = Math.min((score / 500) * 100, 100);

  return (
    <Card className={`${className}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Award size={18} className="text-idecide-accent" />
            <h3 className="font-semibold text-gray-900">iDecide Score</h3>
          </div>
          <button
            type="button"
            onClick={handleRefreshScore}
            disabled={refreshing || !user}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
            title="Recalculate score from your onboarding answers"
            aria-label="Recalculate iDecide score from onboarding answers"
          >
            <RefreshCw size={16} className={`text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {refreshError && (
          <p className="text-xs text-red-600 mb-3" role="alert">
            {refreshError}
          </p>
        )}

        {loading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-16 bg-gray-100 rounded-lg"></div>
            <div className="h-24 bg-gray-100 rounded-lg"></div>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`text-3xl font-bold ${getScoreColor()} leading-none tabular-nums`}>
                  {score}
                </span>
                <span className="text-sm text-gray-500">/ 500</span>
                {scoreChange !== 0 && (
                  <span
                    className={`inline-flex items-center gap-0.5 text-xs font-medium ml-1 ${
                      scoreChange > 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {scoreChange > 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                    {scoreChange > 0 ? '+' : ''}
                    {scoreChange}
                  </span>
                )}
                <span
                  className={`text-sm font-medium px-2 py-1 rounded-full ${
                    score >= 400
                      ? 'bg-green-100 text-green-800'
                      : score >= 250
                        ? 'bg-blue-100 text-blue-800'
                        : score >= 100
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-red-100 text-red-800'
                  }`}
                >
                  {getScoreLabel()}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                <div
                  className="bg-idecide-accent h-2 rounded-full transition-all duration-300"
                  style={{ width: `${scorePercentage}%` }}
                />
              </div>
              <p className="text-xs text-gray-600 mt-2">{getScoreDescription()}</p>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Improve your score</h4>
              {recommendations.map((recommendation, index) => (
                <div key={index} className="flex items-start gap-2 p-2 rounded-lg bg-idecide-light">
                  <Info size={16} className="text-idecide-accent flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-700">{recommendation}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-3 border-t border-gray-100">
              <Link
                to="/dashboard/digital"
                className="text-xs text-[#1E1B4B] hover:text-[#2D2A6A] flex items-center gap-1"
              >
                <CheckCircle size={12} />
                <span>Open Digital Estate to complete more tasks</span>
              </Link>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
